import { LocalizationManager } from '@lib/localization';
import { type Inspection } from '@lib/spproject/types';
import { handleApiResult, isServerError } from '@store/error';
import { apiSlice } from './apiSlice';
import { type LocalizedText } from './poiTypeEndpoints';

/** Mirrors the backend's `reportDesignLimits`. */
export const reportDesignLimits = {
  nameLength: 60,
  descriptionLength: 200,
  pages: 5,
  blocksPerPage: 20,
  fieldItems: 12,
  signatureLines: 4,
  locales: 20,
  /** The seven content sections as compact JSON, in bytes. */
  bytes: 90_000,
} as const;

/**
 * Mirrors the backend's `reportImageLimits`: what an uploaded image of a report may be, for a
 * design's images and a brand's logo alike. PNG or JPEG, which the server tells by the file's
 * first bytes.
 */
export const reportImageLimits = {
  bytes: 1_048_576,
  /** The pixels of one PNG. The server takes a PNG apart on every report; a JPEG has no such limit. */
  pngPixels: 4_000_000,
  perDesign: 50,
} as const;

/**
 * How long one text of a design may get in one locale, by what it is. Mirrors `textLength`
 * in the backend's `report-design.schema.ts`.
 */
export const reportTextLengths = { label: 40, brand: 60, line: 80, heading: 120, paragraph: 2000 } as const;

/** The ranges the request schema holds a block's numbers to: a text's size and a spacer's height in points. */
export const reportNumberRanges = {
  textSize: { min: 7, max: 16 },
  widthPercent: { min: 10, max: 100 },
  spacerHeight: { min: 4, max: 200 },
} as const;

/** Who a design belongs to: the organization when `teamId` is `null`, a team otherwise. */
export interface ReportDesignOwner {
  teamId: string | null;
  /** `null` for the organization, which has no name of its own. */
  teamName: string | null;
}

/** What an owner goes by on screen. */
export const ownerLabel = ({ teamId, teamName }: ReportDesignOwner): string =>
  teamId === null ? LocalizationManager.t('AppSettings_Organization') : teamName ?? '';

/**
 * A design as the list carries it: enough to name it, place it under its owner and gate its
 * actions. The owner is set when the design is created and never changes.
 */
export interface ReportDesignSummary extends ReportDesignOwner {
  _id: string;
  /** Plain text, not a locale map: shown where a design is listed and picked, never printed. */
  name: string;
  description: string | null;
  /** Rises by one on every save. A save sends back the version it was made against. */
  version: number;
  updatedAt: string;
  myPermissions: string[];
}

/*
 * The content of a design. These types mirror `report-design.model.ts` in the backend by
 * hand, as the rest of this file mirrors the API: a key added there is added here, and to
 * the editor's block catalog where an author is to set it.
 */

/**
 * Text an author wrote for a report, by locale. The same shape as a POI type's label, but it
 * may be empty: most text of a design is optional, and a text with nothing in it prints what
 * the report would print without it.
 *
 * It may carry placeholders the report fills in: `{projectName}`, `{inspectionName}`,
 * `{date}`, `{generatedDate}`, `{operator}`, `{completion}`.
 */
export type ReportText = LocalizedText;

/** An image of a design, by the id the server stored it under. */
export interface ReportImageRef {
  imageId: string;
}

/** What a `fields` block can print. `metadata` reads one entry of the inspection's metadata. */
export const reportFieldSources = [
  'projectName', 'inspectionName', 'inspectionDate', 'modifiedDate', 'generatedDate',
  'completion', 'checkpointCount', 'operator', 'metadata',
] as const;
export type ReportFieldSource = typeof reportFieldSources[number];

export const reportBlockTypes = [
  'hero', 'heading', 'text', 'fields', 'image', 'photoGrid', 'signatures', 'divider', 'spacer',
] as const;
export type ReportBlockType = typeof reportBlockTypes[number];

interface Block<T extends ReportBlockType, P> {
  /** Minted here and kept across saves: the editor selects and sorts by it. */
  id: string;
  type: T;
  /** Every key is optional, and so is the object: a block with none draws its defaults. */
  props?: P;
}

export type ReportHeroBlock = Block<'hero', {
  overline?: ReportText;
  title?: ReportText;
  subtitle?: ReportText;
  showRule?: boolean;
  align?: 'left' | 'center';
}>;

export type ReportHeadingBlock = Block<'heading', {
  text?: ReportText;
  /** 1 is a title, 2 a section label. */
  level?: 1 | 2;
}>;

export type ReportTextBlock = Block<'text', {
  content?: ReportText;
  style?: 'normal' | 'muted' | 'italic';
  size?: number;
  align?: 'left' | 'center' | 'right';
}>;

export interface ReportFieldItem {
  source: ReportFieldSource;
  /** Printed over the value in place of the source's own name. A `metadata` item has no other. */
  label?: ReportText;
  /** The id of the metadata entry, for `metadata`. */
  metadataKey?: string;
}

export type ReportFieldsBlock = Block<'fields', {
  items?: ReportFieldItem[];
  columns?: 1 | 2 | 3 | 4;
}>;

export type ReportImageBlock = Block<'image', {
  source?: 'upload' | 'projectThumbnail' | 'logo';
  /** The uploaded image, for `upload`. */
  image?: ReportImageRef | null;
  widthPercent?: number;
  align?: 'left' | 'center' | 'right';
  frame?: boolean;
}>;

/** The inspection's snapshots as a grid. */
export type ReportPhotoGridBlock = Block<'photoGrid', {
  columns?: 1 | 2 | 3;
}>;

export interface ReportSignatureLine {
  label: ReportText;
}

export type ReportSignaturesBlock = Block<'signatures', {
  lines?: ReportSignatureLine[];
  showDate?: boolean;
}>;

export type ReportDividerBlock = Block<'divider', Record<string, never>>;

export type ReportSpacerBlock = Block<'spacer', {
  height?: number;
}>;

export type ReportBlock =
  | ReportHeroBlock
  | ReportHeadingBlock
  | ReportTextBlock
  | ReportFieldsBlock
  | ReportImageBlock
  | ReportPhotoGridBlock
  | ReportSignaturesBlock
  | ReportDividerBlock
  | ReportSpacerBlock;

/** A cover or closing page: blocks an author arranges freely. */
export interface ReportDesignPage {
  id: string;
  background?: 'white' | 'navy';
  /** A page that is not top-aligned is one page and clips what does not fit. */
  verticalAlign?: 'top' | 'center' | 'bottom';
  blocks: ReportBlock[];
}

export const reportChromeSlotTypes = ['none', 'logo', 'brandName', 'text', 'date', 'pageNumbers'] as const;

export interface ReportChromeSlot {
  type: typeof reportChromeSlotTypes[number];
  /** For `text`. */
  text?: ReportText;
}

/** The strip at the top or the bottom of every page that is not a cover. */
export interface ReportChrome {
  enabled: boolean;
  left?: ReportChromeSlot;
  center?: ReportChromeSlot;
  right?: ReportChromeSlot;
}

export interface ReportDesignTheme {
  brandName?: ReportText;
  /** `#rrggbb`. */
  accentColor?: string;
  logo?: ReportImageRef | null;
}

export interface ReportSummarySegment {
  template: 'dashboard' | 'table' | 'minimal' | 'none';
  title?: ReportText;
  showVerdict?: boolean;
  showCompletion?: boolean;
  showBar?: boolean;
  showCounts?: boolean;
}

export interface ReportCheckpointsSegment {
  template: 'detailed' | 'cards' | 'table' | 'grid' | 'none';
  title?: ReportText;
  photos?: 'none' | 'thumbs' | 'large';
  /** Lists only the points in one of these statuses. The summary still counts them all. */
  statuses?: Inspection.NoticeType[];
  showComments?: boolean;
  showDescriptions?: boolean;
  showDates?: boolean;
  /** Lists the inspection's notes after its checkpoints. Without it a design prints no notes. */
  includeNotes?: boolean;
  /** The answers to a step's POI type, where the template shows a step's detail. On unless switched off. */
  showCustomFields?: boolean;
}

/** What a design says a report looks like: everything but its name, its owner and its version. */
export interface ReportDesignContent {
  theme: ReportDesignTheme;
  header: ReportChrome;
  footer: ReportChrome;
  covers: ReportDesignPage[];
  summary: ReportSummarySegment;
  checkpoints: ReportCheckpointsSegment;
  closings: ReportDesignPage[];
}

/**
 * The keys of `ReportDesignContent`, in the model's order. The server measures a design's
 * size over these sections in this order, so whoever measures here names them the same way.
 */
export const reportDesignContentKeys = [
  'theme', 'header', 'footer', 'covers', 'summary', 'checkpoints', 'closings',
] as const satisfies readonly (keyof ReportDesignContent)[];

export interface ReportDesign extends ReportDesignSummary, ReportDesignContent {
  schemaVersion: number;
  createdAt: string;
}

/** A design that ships with the product, to start a new one from. Its texts are locale maps. */
export interface ReportDesignPreset {
  /** What a create request names it by. */
  key: string;
  name: LocalizedText;
  description: LocalizedText;
}

export interface CreateReportDesignInput {
  /** `null` creates a design of the organization. */
  teamId: string | null;
  name: string;
  description?: string | null;
  /** A design never starts empty: it is a preset, or a copy of a design the reader can read. */
  from: { preset: string } | { designId: string };
}

/** The whole design, as `PUT` expects it. Every section is required, and so is the version. */
export type UpdateReportDesignInput = ReportDesignContent & Pick<ReportDesign, 'name' | 'description' | 'version'> & {
  id: string;
};

/** A real inspection to draw a preview from, in place of the built-in sample. */
export interface ReportPreviewSource {
  projectId: string;
  inspectionId: string;
}

/**
 * What a preview of a design is drawn with. Without `draft` it is the stored design, which
 * takes only the right to read it; with one, the right to save it. Without `source` it is
 * drawn over the sample inspection.
 */
export interface ReportDesignPreviewRequest {
  id: string;
  draft?: ReportDesignContent;
  source?: ReportPreviewSource;
  locale?: string;
}

/** The designs of one owner. */
export interface ReportDesignGroup extends ReportDesignOwner {
  designs: ReportDesignSummary[];
}

/**
 * A list of designs by owner, in the order the owners first appear. The API lists the
 * organization's designs first and then each team's by team name, so that is the order of
 * the groups too.
 */
export const groupByOwner = (designs: ReportDesignSummary[]): ReportDesignGroup[] => {
  const groups = new Map<string | null, ReportDesignGroup>();

  designs.forEach((design) => {
    const group = groups.get(design.teamId) ?? { teamId: design.teamId, teamName: design.teamName, designs: [] };

    group.designs.push(design);
    groups.set(design.teamId, group);
  });

  return [...groups.values()];
};

/** The API's answer to a save that claims a version the design has already left. */
export const STALE_VERSION = 412;

/**
 * Whether a copy of a design can be taken for the design as it is stored now, by an editor
 * whose draft is based on version `base`: the version it was read at, or the one its own
 * last save produced.
 *
 * Not a copy older than that. After a save the read of the design can hold one for a while:
 * a request that was on its way before the save and was answered after it. And not the copy
 * the draft is based on either, once the server has `refused` a save of that draft as stale:
 * the stored design has moved on then, and only a copy past the base can be it.
 */
export const isCurrentCopy = (version: number, base: number, refused: boolean): boolean =>
  (refused ? version > base : version >= base);

/**
 * The images a refused save or preview names as not being the design's. The API answers
 * such a body with 400 and `errors: [{ imageId, blockId }]`, `blockId` being `null` for the
 * theme's logo. Empty for any other refusal.
 */
export const missingImagesOf = (failure: unknown): string[] => {
  if (!isServerError(failure) || failure.error.status !== 400 || !Array.isArray(failure.error.data.errors)) return [];

  return failure.error.data.errors.flatMap((entry: unknown) => {
    const imageId = typeof entry === 'object' && entry !== null ? (entry as { imageId?: unknown }).imageId : undefined;

    return typeof imageId === 'string' ? [imageId] : [];
  });
};

/** A binary answer comes as a blob, a refusal as the API's usual JSON. */
export const blobOrError = async (response: Response): Promise<unknown> => (response.ok ? response.blob() : response.json());

export const toObjectUrl = (blob: Blob): string => (URL || webkitURL).createObjectURL(blob);

/**
 * For a query that answers with an object URL: gives the address back once nothing shows it
 * any more. Goes in as the query's `onCacheEntryAdded`.
 */
export const revokeWithCacheEntry = async (
  _arg: unknown,
  { cacheDataLoaded, cacheEntryRemoved }: { cacheDataLoaded: Promise<{ data: string }>; cacheEntryRemoved: Promise<void> },
): Promise<void> => {
  try {
    const { data } = await cacheDataLoaded;

    await cacheEntryRemoved;
    URL.revokeObjectURL(data);
  } catch (_) {
    // The entry went before anything was read into it: there is no address to give back.
  }
};

export const reportDesignApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getReportDesigns: builder.query<ReportDesignSummary[], void>({
      query: () => '/v1/report-designs',
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ _id }) => ({ type: 'ReportDesign' as const, id: _id })),
            { type: 'ReportDesign', id: 'LIST' },
          ]
          : [{ type: 'ReportDesign', id: 'LIST' }],
      onQueryStarted: handleApiResult(),
    }),
    getReportDesignPresets: builder.query<ReportDesignPreset[], void>({
      query: () => '/v1/report-designs/presets',
      onQueryStarted: handleApiResult(),
    }),
    getReportDesign: builder.query<ReportDesign, string>({
      query: (id) => `/v1/report-designs/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'ReportDesign', id }],
      onQueryStarted: async (arg, api) => {
        try {
          await api.queryFulfilled;
        } catch (error) {
          // Not shown: the page that reads a design says itself that there is none, or that
          // it is not the reader's to open, in the reader's language.
          if (isServerError(error) && [403, 404].includes(error.error.status)) return;
        }

        return handleApiResult()(arg, api);
      },
    }),
    addReportDesign: builder.mutation<{ id: string }, CreateReportDesignInput>({
      query: (body) => ({ url: '/v1/report-designs', method: 'POST', body }),
      invalidatesTags: [{ type: 'ReportDesign', id: 'LIST' }],
      onQueryStarted: handleApiResult('Api_ReportDesign_Add_Successful'),
    }),
    updateReportDesign: builder.mutation<void, UpdateReportDesignInput>({
      query: ({ id, ...body }) => ({ url: `/v1/report-designs/${id}`, method: 'PUT', body }),
      // The list carries a tag per design, so this refetches it. RTK Query invalidates for a
      // refused save as well, which is what reads the newer copy after a stale version.
      invalidatesTags: (_result, _error, { id }) => [{ type: 'ReportDesign', id }],
      onQueryStarted: async (arg, api) => {
        try {
          await api.queryFulfilled;

          // What was sent is the stored design now, one version up, and it goes into the
          // design's own read at once. The re-read the tag sets off is held back for as long
          // as any other request is on its way, a preview that is being drawn included, and
          // until it lands that read would still be the copy from before this save: for the
          // editor to go back to on Discard, or to open on after it was left and reopened.
          // The re-read still follows and brings the server's own copy, the time of the
          // change with it.
          //
          // At once is also before the save has answered to whoever sent it. The editor is
          // shown this copy while its own base is still the version before, which is why it
          // judges no copy of the design until its save has ended.
          const { id, version, ...stored } = arg;

          api.dispatch(reportDesignApiSlice.util.updateQueryData('getReportDesign', id, (design) => {
            // A copy read since may be the server's own of this save already, or a later one.
            if (design.version <= version) Object.assign(design, stored, { version: version + 1 });
          }));
        } catch (error) {
          // Not shown here: the editor, the one writer of a design, says in the reader's
          // language what a stale version means for the draft it holds, and keeps saying it
          // until the draft is given up. The server's sentence is English and would be gone
          // in a moment.
          if (isServerError(error) && error.error.status === STALE_VERSION) return;
        }

        return handleApiResult('Api_ReportDesign_Update_Successful')(arg, api);
      },
    }),
    deleteReportDesign: builder.mutation<void, string>({
      query: (id) => ({ url: `/v1/report-designs/${id}`, method: 'DELETE' }),
      // The design's own tag as well as the list's. Its read is dropped with that: nothing
      // on screen reads a design that is deleted from the list, and kept for its unused
      // minute it would open the editor on the deleted design for whoever went back to it.
      // A page that did still read it would have it read again and answered 404, which
      // `getReportDesign` shows no error for and the page says in its own words.
      invalidatesTags: (_result, _error, id) => [{ type: 'ReportDesign', id: 'LIST' }, { type: 'ReportDesign', id }],
      onQueryStarted: handleApiResult('Api_ReportDesign_Delete_Successful'),
    }),
    /**
     * Stores an image for a design and answers the id to refer to it by. Nothing refers to it
     * yet: the draft that names it is saved by a `PUT` of its own, and the server keeps an
     * unreferenced image for a day.
     */
    uploadReportDesignImage: builder.mutation<{ imageId: string }, { designId: string; file: File }>({
      query: ({ designId, file }) => {
        const body = new FormData();

        body.append('file', file);

        return { url: `/v1/report-designs/${designId}/images`, method: 'POST', body };
      },
      onQueryStarted: handleApiResult(),
    }),
    /**
     * An image of a design as an address a picture can show. The API hands the bytes only to
     * a request that carries the session's token, which an `<img>` pointed at it would not, so
     * they are read here and kept under an object URL for as long as something shows them.
     *
     * No error is shown from here: an image the server no longer has is a state of the
     * control that asked for it, not a failure to announce.
     */
    getReportDesignImage: builder.query<string, { designId: string; imageId: string }>({
      query: ({ designId, imageId }) => ({
        url: `/v1/report-designs/${designId}/images/${imageId}`,
        responseHandler: blobOrError,
      }),
      transformResponse: toObjectUrl,
      onCacheEntryAdded: revokeWithCacheEntry,
    }),
    /**
     * Draws a design as a PDF and answers an object URL for it, which whoever asked gives
     * back with `URL.revokeObjectURL`.
     *
     * A mutation, although it changes nothing: as a query every state of a draft would be a
     * cache key. And no error is shown from here, because a preview is asked for without a
     * click: the component that shows the picture says that a draft cannot be drawn (422) or
     * that another preview is still being drawn (429) in its own place.
     */
    previewReportDesign: builder.mutation<string, ReportDesignPreviewRequest>({
      query: ({ id, ...body }) => ({
        url: `/v1/report-designs/${id}/preview`,
        method: 'POST',
        body,
        responseHandler: blobOrError,
      }),
      transformResponse: toObjectUrl,
    }),
  }),
});

export const {
  useGetReportDesignsQuery,
  useGetReportDesignPresetsQuery,
  useGetReportDesignQuery,
  useGetReportDesignImageQuery,
  useAddReportDesignMutation,
  useUpdateReportDesignMutation,
  useDeleteReportDesignMutation,
  useUploadReportDesignImageMutation,
  usePreviewReportDesignMutation,
} = reportDesignApiSlice;
